import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Lock, Check, X, RotateCcw, Ban } from "lucide-react";
import { useApartments } from "../hooks/useApartments";
import { AdminStats } from "../components/AdminStats";
import { AdminApartments } from "../components/AdminApartments";
import { AdminReviews } from "../components/AdminReviews";
import { AdminSecurity } from "../components/AdminSecurity";
import { AdminSmoobu } from "../components/AdminSmoobu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vn_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const STATUS_LABEL = { requested: "Richiesta", confirmed: "Confermata", cancelled: "Annullata", blocked: "Bloccata" };
const STATUS_CLS = {
  requested: "bg-[#C29B62]/15 text-[#8a6a3a] border-[#C29B62]/40",
  confirmed: "bg-[#1C3829]/10 text-[#1C3829] border-[#1C3829]/30",
  cancelled: "bg-red-900/10 text-red-800 border-red-800/30",
  blocked: "bg-[#1A1A1A]/10 text-[#1A1A1A] border-[#1A1A1A]/30",
};

export default function Admin() {
  const APARTMENTS = useApartments();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [bookings, setBookings] = useState([]);
  const [block, setBlock] = useState({ apartment_id: APARTMENTS[0].id, check_in: "", check_out: "", note: "" });
  const [blocking, setBlocking] = useState(false);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }),
    []
  );

  const loadBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: headers() });
      setBookings(res.data);
    } catch {
      toast.error("Errore nel caricamento delle richieste");
    }
  }, [headers]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: headers() })
      .then((res) => {
        setUser(res.data);
        loadBookings();
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, [headers, loadBookings]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser({ email: res.data.email, name: res.data.name });
      loadBookings();
      toast.success("Accesso effettuato");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setBookings([]);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/status`, { status }, { headers: headers() });
      toast.success(
        status === "confirmed" ? "Prenotazione confermata" : status === "cancelled" ? "Prenotazione annullata" : "Stato aggiornato"
      );
      loadBookings();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doBlock = async (e) => {
    e.preventDefault();
    setBlocking(true);
    try {
      await axios.post(`${API}/admin/blocks`, block, { headers: headers() });
      toast.success("Date bloccate");
      setBlock({ ...block, check_in: "", check_out: "", note: "" });
      loadBookings();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBlocking(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-3 text-base font-light placeholder:text-[#1A1A1A]/35 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center" data-testid="admin-loading">
        <p className="font-serif-display text-2xl text-[#1C3829]">Villa Navagero</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border border-[#C29B62]/25 bg-white/60 p-10">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-[#C29B62]" />
            <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">Area riservata</p>
          </div>
          <h1 className="mt-4 text-4xl font-light tracking-tight text-[#1C3829] font-serif-display">Villa Navagero</h1>
          <form onSubmit={doLogin} className="mt-10 space-y-8" data-testid="admin-login-form">
            <div>
              <label className={labelCls} htmlFor="ad-email">Email</label>
              <input
                id="ad-email"
                data-testid="admin-email-input"
                type="email"
                required
                className={inputCls}
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="ad-pass">Password</label>
              <input
                id="ad-pass"
                data-testid="admin-password-input"
                type="password"
                required
                className={inputCls}
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              />
            </div>
            {loginError && (
              <p className="text-sm text-red-700" data-testid="admin-login-error">{loginError}</p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              className="w-full bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:text-[#C29B62] transition-colors"
            >
              Accedi
            </button>
          </form>
          <a href="/" data-testid="admin-back-home" className="mt-8 inline-block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 hover:text-[#1C3829] transition-colors">
            ← Torna al sito
          </a>
        </div>
      </div>
    );
  }

  const active = bookings.filter((b) => b.status !== "cancelled");

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="admin-dashboard">
      <header className="border-b border-[#C29B62]/20 bg-[#F7F5F0]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <span className="font-serif-display text-xl text-[#1C3829]">Villa Navagero · Area Riservata</span>
          <div className="flex items-center gap-6">
            <span className="hidden sm:block text-xs text-[#1A1A1A]/50" data-testid="admin-user-email">{user.email}</span>
            <a href="/" className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 hover:text-[#1C3829] transition-colors">Sito</a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 hover:text-red-800 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-14">
        <AdminStats headers={headers} />

        <AdminApartments headers={headers} />

        <AdminReviews headers={headers} />

        <AdminSmoobu headers={headers} />

        <section data-testid="admin-block-section" className="border border-[#C29B62]/25 bg-white/60 p-8">
          <div className="flex items-center gap-3">
            <Ban className="w-4 h-4 text-[#C29B62]" />
            <h2 className="text-2xl font-light text-[#1C3829] font-serif-display">Blocca date manualmente</h2>
          </div>
          <form onSubmit={doBlock} className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-6 items-end" data-testid="admin-block-form">
            <div>
              <label className={labelCls}>Appartamento</label>
              <select
                data-testid="admin-block-apartment"
                className={`${inputCls} bg-transparent`}
                value={block.apartment_id}
                onChange={(e) => setBlock({ ...block, apartment_id: e.target.value })}
              >
                {APARTMENTS.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Dal</label>
              <input data-testid="admin-block-from" type="date" required className={inputCls} value={block.check_in} onChange={(e) => setBlock({ ...block, check_in: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Al</label>
              <input data-testid="admin-block-to" type="date" required className={inputCls} value={block.check_out} onChange={(e) => setBlock({ ...block, check_out: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Nota (facoltativa)</label>
              <input data-testid="admin-block-note" className={inputCls} value={block.note} onChange={(e) => setBlock({ ...block, note: e.target.value })} />
            </div>
            <button
              data-testid="admin-block-submit"
              type="submit"
              disabled={blocking}
              className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:text-[#C29B62] transition-colors disabled:opacity-40"
            >
              Blocca
            </button>
          </form>
        </section>

        <section data-testid="admin-bookings-section">
          <h2 className="text-2xl font-light text-[#1C3829] font-serif-display">
            Richieste e prenotazioni <span className="text-[#C29B62]">({active.length})</span>
          </h2>
          <div className="mt-6 overflow-x-auto border border-[#C29B62]/25 bg-white/60">
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.15em] text-[#1A1A1A]/50 border-b border-[#C29B62]/25">
                  <th className="px-5 py-4">Ospite</th>
                  <th className="px-5 py-4">Appartamento</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Notti</th>
                  <th className="px-5 py-4">Totale</th>
                  <th className="px-5 py-4">Stato</th>
                  <th className="px-5 py-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="border-b border-[#C29B62]/10 last:border-0">
                    <td className="px-5 py-4">
                      <p className="font-medium text-[#1A1A1A]">{b.name}</p>
                      {b.email && <p className="text-xs text-[#1A1A1A]/50">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>}
                      {b.message && <p className="text-xs italic text-[#1A1A1A]/40 mt-1 max-w-[16rem] truncate" title={b.message}>{b.message}</p>}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">{b.apartment_name}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                    <td className="px-5 py-4">{b.nights}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{b.estimated_total ? `€${b.estimated_total}` : "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span data-testid={`admin-booking-status-${b.id}`} className={`inline-block px-3 py-1 text-xs border ${STATUS_CLS[b.status] || ""}`}>
                          {STATUS_LABEL[b.status] || b.status}
                        </span>
                        {b.source === "smoobu" && (
                          <span className="inline-block px-3 py-1 text-xs border border-[#C29B62]/40 text-[#8a6a3a]">Smoobu</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        {b.source !== "smoobu" && b.status !== "confirmed" && b.status !== "cancelled" && (
                          <button
                            data-testid={`admin-confirm-${b.id}`}
                            onClick={() => setStatus(b.id, "confirmed")}
                            title="Conferma"
                            className="p-2 border border-[#1C3829]/30 text-[#1C3829] hover:bg-[#1C3829] hover:text-[#F7F5F0] transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {b.source !== "smoobu" && b.status !== "cancelled" && (
                          <button
                            data-testid={`admin-cancel-${b.id}`}
                            onClick={() => setStatus(b.id, "cancelled")}
                            title="Annulla"
                            className="p-2 border border-red-800/30 text-red-800 hover:bg-red-800 hover:text-white transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {b.source !== "smoobu" && b.status === "cancelled" && (
                          <button
                            data-testid={`admin-reopen-${b.id}`}
                            onClick={() => setStatus(b.id, "requested")}
                            title="Ripristina"
                            className="p-2 border border-[#C29B62]/40 text-[#8a6a3a] hover:bg-[#C29B62] hover:text-white transition-colors"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-[#1A1A1A]/40" data-testid="admin-bookings-empty">
                      Nessuna richiesta al momento
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <AdminSecurity headers={headers} />
      </main>
    </div>
  );
}
